import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BreadcrumbNavigation from 'components/ui/BreadcrumbNavigation';
import ServiceHeader from './components/ServiceHeader';
import ServiceFilters from './components/ServiceFilters';
import ServiceGrid from './components/ServiceGrid';
import ServiceModal from './components/ServiceModal';
import Button from 'components/ui/Button';
import { fetchCategories } from 'services/categoryService';
import { useSelector } from 'react-redux';
import { getBusinessServices, updateBusinessService, deleteBusinessService, createBusinessService } from 'services/businessService';
import ConfirmationModal from 'components/ui/ConfirmationModal';
import {toast} from "react-toastify";
import Icon from 'components/AppIcon';

const ServiceManagement = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    sortBy: 'name'
  });

  const [categories, setCategories] = useState([]);
  const selectedBusiness = useSelector((state) => state?.business.selectedBusiness);
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState(null);
  const [modalState, setModalState] = useState({
    title: '',
    description: '',
    isOpen: false
  });

  // Calculate service statistics
  const serviceStats = {
    total: services?.length,
    active: services?.filter(s => s?.active)?.length,
    inactive: services?.filter(s => !s?.active)?.length,
    averagePrice: (services?.reduce((sum, s) => sum + s?.currentAmount, 0) / services?.length)?.toFixed(2),
    // revenueByCategory: [
    //   { name: 'Basic', revenue: '2,450', percentage: 35, color: '#2563EB' },
    //   { name: 'Premium', revenue: '3,200', percentage: 45, color: '#059669' },
    //   { name: 'Detailing', revenue: '1,800', percentage: 25, color: '#D97706' },
    //   { name: 'Specialty', revenue: '800', percentage: 15, color: '#DC2626' },
    //   { name: 'Maintenance', revenue: '600', percentage: 10, color: '#7C3AED' }
    // ]
  };

  const loadServices = async()=>{
    if(!selectedBusiness?.businessId) return;
   
    const response = await getBusinessServices(selectedBusiness?.businessId);
    if(response.success && response.data){
      setServices(response.data);
    }

  }

  const loadCategories = async () => {
    if(!selectedBusiness) return;

    const response = await fetchCategories(selectedBusiness?.businessId);
    if (response?.data && response?.success) {
      const structuredCategories = response?.data?.map(cat => ({ value: cat?.id, label: cat?.name }));
      setCategories(structuredCategories);
    }

  }

  useEffect(() => {
    setIsLoading(true);
    loadCategories();
    loadServices();
    setIsLoading(false);
  }, []);

  // Filter and sort services
  const filteredServices = services?.filter(service => {
    const matchesSearch = service?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         service?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         service?.category?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesCategory = !filters?.category || service?.categoryId === filters?.category;
    const matchesStatus = !filters?.status || 
                         (filters?.status === 'active' && service?.isActive) ||
                         (filters?.status === 'inactive' && !service?.isActive);

    return matchesSearch && matchesCategory && matchesStatus;
  })?.sort((a, b) => {
    switch (filters?.sortBy) {
      case 'price-low':
        return a?.price - b?.price;
      case 'price-high':
        return b?.price - a?.price;
      case 'duration':
        return a?.duration - b?.duration;
      case 'popularity':
        const popularityOrder = { high: 3, medium: 2, low: 1 };
        return popularityOrder?.[b?.popularity] - popularityOrder?.[a?.popularity];
      default:
        return a?.name?.localeCompare(b?.name);
    }
  });

  const handleAddService = () => {
    setSelectedService(null);
    setIsModalOpen(true);
  };

  const handleEditService = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const showDeleteConfirmation = (serviceId) => {
    setServiceId(serviceId);
    setModalState({...modalState, isOpen: true});
  };

  const handleToggleStatus = async (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    
    if(!service) return;

    const updatedService = { ...service, active: !service.active, category: categories.find(x=>x.label == service?.category)?.value };
    setSelectedService(service);
    handleSaveService(updatedService); // Reuse modal for update
  };

  const handleSaveService = async (serviceData) => {
    if (selectedService) {
      // Update existing service
      const response = await updateBusinessService(selectedBusiness.businessId, serviceData, selectedService.id, serviceData.category);
      if(response.success && response.data){
        // setServices(prev => prev?.map(service => 
        // service?.id === selectedService?.id 
        //   ? { ...serviceData, id: selectedService?.id, category: categories.find(x=>x.value == serviceData?.category)?.label  }
        //   : service
        // ));
        loadServices();
      }
    } else {
      // Add new service
      const response = await createBusinessService(selectedBusiness.businessId, serviceData, serviceData.category);
      if(response.success && response.data){
        //setServices(prev => [...prev, { ...response.data, category: categories.find(x=>x.value == serviceData?.category)?.label  }]);
        //setServices(prev => [...prev, { ...serviceData, category: categories.find(x=>x.value == serviceData?.category)?.label  }]);
        toast.success(response?.message || "Service created successfully");
        loadServices();
      }
      else{
        toast.error(response?.message || "Failed to create service. Please try again.");
      }
      //setServices(prev => [...prev, { ...serviceData, id: Date.now() }]);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      category: '',
      status: '',
      sortBy: 'name'
    });
    setSearchTerm('');
  };

  const handleDeleteConfirmation = async () => {
    if(!setServiceId) return;
    // Close Modal
    setModalState({...modalState, isOpen: false});
    //Call API to delete
    setIsLoading(true);
    const results = await deleteBusinessService(selectedBusiness.businessId, serviceId);

    if(results && results.success){
      setServices(prev => prev?.filter(s => s?.id !== serviceId));
      //Reset serviceId
      setServiceId(null);
      toast.success(results?.message || "Service deleted successfully");
    }
    else{
      toast.error(results?.message || "Failed to delete service. Please try again.");
    }
    setIsLoading(false);
  }

  const handleDeleteCancellation = () => {
    //Close Modal
    setModalState({...modalState, isOpen: false});
    //Reset serviceId
    setServiceId(null);
  }

  return (
    <>
      <div className="p-4 lg:p-6 space-y-6">
        {/* Breadcrumb */}
        <BreadcrumbNavigation />

        {/* Service Header */}
        <ServiceHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onAddService={handleAddService}
          totalServices={services?.length}
          activeServices={services?.filter(s => s?.active)?.length}
        />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <ServiceFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              serviceStats={serviceStats}
              categoryOptions={categories}
            />
          </div>

          {/* Services Grid */}
          <div className="lg:col-span-3">
            <ServiceGrid
              services={filteredServices}
              onEdit={handleEditService}
              onAdd={handleAddService}
              onDelete={showDeleteConfirmation}
              onToggleStatus={handleToggleStatus}
              isLoading={isLoading}
              searchTerm={searchTerm}
            />
          </div>
        </div>
      </div>
      {/* Confirmation Modal */}
      <ConfirmationModal
        onConfirmation={handleDeleteConfirmation}
        onCancellation={handleDeleteCancellation}
        title={modalState?.title || "Delete Service"}
        description={modalState?.description || "Are you sure you want to delete this service? This action cannot be undone."}
        open={modalState?.isOpen}
      />
      {/* Service Modal */}
      <ServiceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        service={selectedService}
        onSave={handleSaveService}
        categories={categories}
      />
      {/* Floating Action Button (Mobile) */}
      {/* <div className="fixed bottom-6 right-6 lg:hidden">
        <Button
          onClick={handleAddService}
          size="lg"
          className="rounded-full w-14 h-14 shadow-lg"
          iconName="Trash2"
          iconSize={8}
        >
          
        </Button>
      </div> */}
    </>
  );
};

export default ServiceManagement;